import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { RefreshCw, Plus, Trash2, Eye, EyeOff, ArrowLeft, Key, Activity, TrendingUp } from 'lucide-react';
import api from '../lib/api';
import { asArray } from '../lib/safeData';
import { StatusBadge } from '../components/StatusBadge';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import AppGrowthPanel from '../components/AppGrowthPanel';

function TabButton({ active, onClick, children }) {
  return (
    <button onClick={onClick} className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${active ? 'bg-primary/15 text-primary border border-primary/20' : 'text-muted-foreground hover:text-foreground hover:bg-white/5'}`}>
      {children}
    </button>
  );
}

export default function AppDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [app, setApp] = useState(null);
  const [credentials, setCredentials] = useState([]);
  const [healthChecks, setHealthChecks] = useState([]);
  const [tab, setTab] = useState(() => searchParams.get('tab') === 'growth' ? 'growth' : 'overview');
  const [checking, setChecking] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showAddCred, setShowAddCred] = useState(false);
  const [newCred, setNewCred] = useState({ key_name: '', key_value: '' });
  const [showValues, setShowValues] = useState({});
  const [healthPath, setHealthPath] = useState('');
  const [savingHealthPath, setSavingHealthPath] = useState(false);

  const fetchAll = async () => {
    try {
      const [appRes, credsRes, hcRes] = await Promise.all([
        api.get(`/apps/${id}`),
        api.get(`/apps/${id}/credentials`),
        api.get(`/apps/${id}/health-checks`),
      ]);
      setApp(appRes.data && typeof appRes.data === 'object' ? appRes.data : null);
      setCredentials(asArray(credsRes.data));
      setHealthChecks(asArray(hcRes.data));
    } catch { toast.error('Failed to load app'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, [id]);
  useEffect(() => {
    if (searchParams.get('tab') === 'growth') setTab('growth');
  }, [searchParams]);
  useEffect(() => { setHealthPath(app?.health_path || ''); }, [app?.id, app?.health_path]);

  const runCheck = async () => {
    setChecking(true);
    try {
      await api.post(`/apps/${id}/health-check`);
      toast.success('Health check complete');
      await fetchAll();
    } catch { toast.error('Health check failed'); }
    finally { setChecking(false); }
  };

  const saveHealthPath = async (e) => {
    e.preventDefault();
    setSavingHealthPath(true);
    try {
      const res = await api.put(`/apps/${id}`, { health_path: healthPath.trim() || null });
      setApp(res.data);
      toast.success('Health endpoint saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save health endpoint');
    } finally {
      setSavingHealthPath(false);
    }
  };

  const addCredential = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post(`/apps/${id}/credentials`, newCred);
      toast.success('Credential saved');
      setCredentials([...credentials.filter(c => c.key_name !== newCred.key_name), res.data]);
      setNewCred({ key_name: '', key_value: '' });
      setShowAddCred(false);
    } catch { toast.error('Failed to save credential'); }
  };

  const openCredentialForm = () => {
    const keyName = app?.connector_type === 'smart_qr' ? 'ops_api_key' : '';
    setNewCred({ key_name: keyName, key_value: '' });
    setShowAddCred(!showAddCred);
  };

  const deleteCredential = async (credId) => {
    if (!window.confirm('Delete this credential?')) return;
    try {
      await api.delete(`/apps/${id}/credentials/${credId}`);
      toast.success('Credential removed');
      setCredentials(credentials.filter(c => c.id !== credId));
    } catch { toast.error('Failed to delete credential'); }
  };

  if (loading) return <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  if (!app) return <div className="text-center py-16 text-muted-foreground">App not found</div>;

  const latestHealth = healthChecks[0];

  return (
    <div className="space-y-6 fade-in" data-testid="app-detail-page">
      {/* Header */}
      <div className="flex items-start gap-4">
        <button onClick={() => navigate('/apps')} className="mt-1 text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-heading text-2xl font-bold text-foreground">{app.name}</h1>
            <StatusBadge status={latestHealth?.status || 'unknown'} />
            <span className="text-xs px-2 py-0.5 rounded-full bg-secondary/50 text-muted-foreground font-mono">{app.environment}</span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-secondary/50 text-muted-foreground font-mono">{app.connector_type}</span>
          </div>
          <div className="text-sm text-muted-foreground font-mono mt-1">{app.base_url}</div>
          {app.notes && <div className="text-sm text-muted-foreground mt-1">{app.notes}</div>}
        </div>
        <button
          onClick={runCheck}
          disabled={checking}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary/10 hover:bg-primary/20 border border-primary/20 text-primary text-sm font-medium transition-colors"
          data-testid="run-health-check-btn"
        >
          <RefreshCw className={`w-4 h-4 ${checking ? 'animate-spin' : ''}`} />
          Health Check
        </button>
      </div>

      {/* Current status card */}
      {latestHealth && (
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: 'Status', value: latestHealth.status.toUpperCase(), color: latestHealth.status === 'up' ? 'text-status-up' : latestHealth.status === 'degraded' ? 'text-status-degraded' : 'text-status-down' },
            { label: 'Response Time', value: latestHealth.response_time_ms != null ? `${latestHealth.response_time_ms}ms` : '—', color: 'text-foreground' },
            { label: 'Last Checked', value: formatDistanceToNow(new Date(latestHealth.checked_at), { addSuffix: true }), color: 'text-foreground' },
          ].map(stat => (
            <div key={stat.label} className="glass rounded-xl border border-white/5 p-4">
              <div className="text-xs text-muted-foreground font-mono">{stat.label}</div>
              <div className={`text-lg font-heading font-bold mt-1 ${stat.color} font-mono`}>{stat.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2" data-testid="app-detail-tabs">
        <TabButton active={tab === 'overview'} onClick={() => setTab('overview')}>Overview</TabButton>
        <TabButton active={tab === 'credentials'} onClick={() => setTab('credentials')}>
          <span className="flex items-center gap-1.5"><Key className="w-3 h-3" /> Credentials ({credentials.length})</span>
        </TabButton>
        <TabButton active={tab === 'history'} onClick={() => setTab('history')}>
          <span className="flex items-center gap-1.5"><Activity className="w-3 h-3" /> Health History ({healthChecks.length})</span>
        </TabButton>
        <TabButton active={tab === 'growth'} onClick={() => setTab('growth')}>
          <span className="flex items-center gap-1.5"><TrendingUp className="w-3 h-3" /> Growth</span>
        </TabButton>
      </div>

      {/* Tab content */}
      {tab === 'overview' && (
        <div className="glass rounded-xl border border-white/5 p-5 space-y-4">
          <h2 className="font-heading font-bold text-foreground">App Information</h2>
          <div className="grid grid-cols-2 gap-4 text-sm">
            {[
              { label: 'App ID', value: app.id },
              { label: 'Type', value: app.app_type },
              { label: 'Connector', value: app.connector_type },
              { label: 'Auth Type', value: app.auth_type },
              { label: 'Environment', value: app.environment },
              { label: 'Status', value: app.enabled ? 'Enabled' : 'Disabled' },
              { label: 'Created', value: format(new Date(app.created_at), 'MMM d, yyyy HH:mm') },
              { label: 'Base URL', value: app.base_url },
              { label: 'Health Endpoint', value: app.health_path ? `${app.base_url.replace(/\/$/, '')}${app.health_path}` : app.base_url },
            ].map(({ label, value }) => (
              <div key={label}>
                <div className="text-xs text-muted-foreground">{label}</div>
                <div className="text-foreground font-mono text-xs mt-0.5 break-all">{value}</div>
              </div>
            ))}
          </div>
          <form onSubmit={saveHealthPath} className="border-t border-white/5 pt-4">
            <label className="text-xs text-muted-foreground">Health Path</label>
            <div className="flex gap-2 mt-1">
              <input
                value={healthPath}
                onChange={e => setHealthPath(e.target.value)}
                placeholder="/api/health"
                className="flex-1 bg-secondary/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-foreground font-mono placeholder-muted-foreground outline-none focus:border-primary/50 transition-colors"
                data-testid="app-health-path-edit-input"
              />
              <button
                type="submit"
                disabled={savingHealthPath}
                className="px-4 py-2 rounded-lg bg-primary hover:bg-primary/90 text-white text-sm font-medium transition-colors disabled:opacity-50"
                data-testid="save-health-path-btn"
              >
                {savingHealthPath ? 'Saving...' : 'Save'}
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Relative to the base URL. Leave empty to check the homepage.</p>
          </form>
          {app.notes && (
            <div>
              <div className="text-xs text-muted-foreground">Notes</div>
              <div className="text-sm text-foreground mt-0.5">{app.notes}</div>
            </div>
          )}
        </div>
      )}

      {tab === 'credentials' && (
        <div className="glass rounded-xl border border-white/5 p-5 space-y-4" data-testid="credentials-tab">
          <div className="flex items-center justify-between">
            <h2 className="font-heading font-bold text-foreground">Credential Vault</h2>
            <button onClick={openCredentialForm} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/10 hover:bg-primary/20 border border-primary/20 text-primary text-xs font-medium transition-colors" data-testid="add-credential-btn">
              <Plus className="w-3 h-3" />
              Add Credential
            </button>
          </div>

          {app.connector_type === 'smart_qr' && (
            <div className="rounded-lg border border-primary/20 bg-primary/5 p-3 text-xs text-muted-foreground" data-testid="smart-qr-credential-help">
              <p className="font-medium text-foreground">Secure Smart QR connection</p>
              <p className="mt-1">Mayr Ops stores the generated <span className="font-mono">ops_api_key</span> encrypted and uses it only to read onboarding and payment readiness. Stripe and Google credentials stay in their own protected connections.</p>
            </div>
          )}

          {showAddCred && (
            <form onSubmit={addCredential} className="bg-secondary/20 rounded-lg p-4 space-y-3 border border-white/5">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground">Key Name</label>
                  <input required readOnly={app.connector_type === 'smart_qr'} value={newCred.key_name} onChange={e => setNewCred({ ...newCred, key_name: e.target.value })} placeholder="api_key" className="mt-1 w-full bg-secondary/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-foreground font-mono placeholder-muted-foreground outline-none focus:border-primary/50 transition-colors" data-testid="cred-key-name-input" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Value</label>
                  <input required type="password" value={newCred.key_value} onChange={e => setNewCred({ ...newCred, key_value: e.target.value })} placeholder="••••••••" className="mt-1 w-full bg-secondary/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-foreground font-mono placeholder-muted-foreground outline-none focus:border-primary/50 transition-colors" data-testid="cred-key-value-input" />
                </div>
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={() => setShowAddCred(false)} className="flex-1 py-1.5 rounded-lg border border-white/10 text-xs text-muted-foreground">Cancel</button>
                <button type="submit" className="flex-1 py-1.5 rounded-lg bg-primary hover:bg-primary/90 text-white text-xs font-medium transition-colors" data-testid="save-credential-btn">Save</button>
              </div>
            </form>
          )}

          {credentials.length === 0 ? (
            <p className="text-muted-foreground text-sm text-center py-6">{app.connector_type === 'smart_qr' ? 'Smart QR is registered but not securely connected yet.' : 'No credentials stored. Add API keys or tokens to authenticate connector actions.'}</p>
          ) : (
            <div className="space-y-2">
              {credentials.map(cred => (
                <div key={cred.id} className="flex items-center gap-3 px-3 py-2.5 bg-secondary/20 rounded-lg" data-testid={`credential-item-${cred.id}`}>
                  <Key className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                  <span className="text-sm font-mono text-foreground">{cred.key_name}</span>
                  <span className="flex-1 text-sm font-mono text-muted-foreground">
                    {showValues[cred.id] ? cred.key_value_masked : cred.key_value_masked}
                  </span>
                  <button onClick={() => setShowValues({ ...showValues, [cred.id]: !showValues[cred.id] })} className="text-muted-foreground hover:text-foreground transition-colors">
                    {showValues[cred.id] ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                  <button onClick={() => deleteCredential(cred.id)} className="text-muted-foreground hover:text-destructive transition-colors" data-testid={`delete-cred-btn-${cred.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'history' && (
        <div className="glass rounded-xl border border-white/5 p-5" data-testid="health-history-tab">
          <h2 className="font-heading font-bold text-foreground mb-4">Health Check History</h2>
          {healthChecks.length === 0 ? (
            <p className="text-muted-foreground text-sm text-center py-6">No health checks yet. Run one to start tracking.</p>
          ) : (
            <div className="space-y-2">
              {healthChecks.map(hc => (
                <div key={hc.id} className="flex items-center gap-3 px-3 py-2.5 bg-secondary/20 rounded-lg" data-testid={`health-check-row-${hc.id}`}>
                  <StatusBadge status={hc.status} />
                  <span className="flex-1 text-sm text-muted-foreground">{hc.message}</span>
                  {hc.response_time_ms != null && <span className="text-xs font-mono text-muted-foreground">{hc.response_time_ms}ms</span>}
                  <span className="text-xs font-mono text-muted-foreground">{format(new Date(hc.checked_at), 'MMM d HH:mm')}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'growth' && <AppGrowthPanel app={app} />}
    </div>
  );
}
